import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Copy, Download, Eye, Pencil } from "lucide-react";

import { api, openProtected } from "@/lib/api";
import { DossierItem } from "@/components/dossier-item";
import { parseTiers, TierEditor, toTierS } from "@/components/tier-editor";
import { useTemplates } from "@/components/tier-templates";
import { Header } from "@/components/header";
import { Badge, Button, Card, ErrorView, Loading, Row } from "@/components/kit";
import { Sheet } from "@/components/sheet";
import { useToast } from "@/components/toast";
import { C, date, dateTime, eur } from "@/lib/format";

const ACTIONS = {
  rib_view: "Consultation (gérant)", rib_view_self: "Consultation (agent)", rib_update: "Modification", rib_pdf_update: "Dépôt PDF", rib_pdf_download: "Téléchargement PDF",
};

function RibCard({ d, id }) {
  const toast = useToast();
  const [rib, setRib] = useState(null);
  const [loading, setLoading] = useState(false);
  const showRib = async () => {
    setLoading(true);
    try {
      setRib(await api(`/admin/agents/${id}/rib`));
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Card className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="t-h3">Coordonnées bancaires</h2>
        <Badge testID="admin-agent-rib-status" label={d.rib.has_rib ? "RIB renseigné" : "RIB manquant"} color={d.rib.has_rib ? C.success : C.warning} />
      </div>
      {!d.can_view_rib ? <p className="t-caption">Accès réservé à l'administrateur principal.</p> : !d.rib.has_rib ? <p className="t-caption">Aucun RIB enregistré.</p> : rib ? (
        <>
          <Row label="Titulaire" value={rib.rib.holder} />
          <Row label="IBAN" value={rib.rib.iban} testID="admin-agent-iban" />
          <Row label="BIC" value={rib.rib.bic} />
          <Row label="Mis à jour" value={dateTime(rib.rib.updated_at)} />
          {rib.rib.pdf_file_id && <Button testID="admin-agent-rib-pdf" title="Télécharger le RIB PDF" icon={Download} variant="secondary" onClick={() => openProtected(`/files/${rib.rib.pdf_file_id}`).catch((e) => toast(e.message, "error"))} />}
          <p className="t-label mt-2">Historique des modifications</p>
          {rib.history.map((h, i) => <p key={i} className="t-caption">{dateTime(h.at)} · {h.action}{h.iban_last4 ? ` (…${h.iban_last4})` : ""}</p>)}
          <p className="t-label mt-2">Journal des accès</p>
          {rib.access_logs.slice(0, 10).map((h, i) => <p key={i} className="t-caption">{dateTime(h.at)} · {ACTIONS[h.action] ?? h.action} · {h.actor_name}</p>)}
        </>
      ) : (
        <>
          <p className="t-caption">{d.rib.iban_masked ?? d.rib.pdf_name}</p>
          <Button testID="admin-agent-show-rib" title="Afficher le RIB (consultation journalisée)" icon={Eye} variant="secondary" loading={loading} onClick={showRib} />
        </>
      )}
    </Card>
  );
}

function TiersSheet({ open, onClose, d, id, tiers, setTiers, onSaved }) {
  const templates = useTemplates();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await api(`/admin/agents/${id}/tiers`, { method: "PUT", body: { tiers: parseTiers(tiers) } });
      await onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Sheet visible={open} onClose={onClose} title="Barème de l'agent" testID="agent-tiers-sheet">
      <div className="flex flex-col gap-3">
        <p className="t-caption">Pourcentage appliqué au montant total signé selon le palier, uniquement pour {d?.agent.name}. Laissez « max » vide pour le dernier palier.</p>
        {(templates.data ?? []).length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="t-label">Partir d'un modèle (vous pouvez ensuite l'ajuster)</span>
            <div className="flex flex-wrap gap-2">
              {templates.data.map((tp) => (
                <Button key={tp.id} testID={`agent-tiers-template-${tp.id}`} title={tp.name} icon={Copy} variant="secondary" onClick={() => { setTiers(toTierS(tp.tiers)); setError(""); }} />
              ))}
            </div>
          </div>
        )}
        <TierEditor tiers={tiers} onChange={(t) => { setTiers(t); setError(""); }} prefix="agent-tier" />
        {error ? <p data-testid="agent-tiers-error" className="text-sm text-[#EB5757]">{error}</p> : null}
        <Button testID="agent-tiers-save" title="Enregistrer le barème" loading={saving} onClick={save} />
      </div>
    </Sheet>
  );
}

export default function AdminAgent() {
  const { id } = useParams();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-agent", id], queryFn: () => api(`/admin/agents/${id}`) });
  const [tiersOpen, setTiersOpen] = useState(false);
  const [tiers, setTiers] = useState([]);
  const d = q.data;

  const onSaved = async () => {
    await q.refetch();
    qc.invalidateQueries({ queryKey: ["admin-dossier"] });
    setTiersOpen(false);
    toast("Barème de l'agent enregistré.");
  };

  return (
    <div data-testid="admin-agent-screen" className="flex flex-col">
      <Header title={d?.agent.name ?? "Agent"} subtitle={d ? `Inscrit le ${date(d.agent.created_at)}` : undefined} />
      {q.isLoading ? <Loading /> : q.isError || !d ? <ErrorView message="Agent introuvable." onRetry={() => q.refetch()} /> : (
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-4 pb-8 md:p-8">
          <div className="grid items-start gap-4 lg:grid-cols-2">
            <div className="flex flex-col gap-4">
              <Card>
                <h2 className="t-h3 mb-2">Identité</h2>
                <Row label="E-mail" value={d.agent.email} testID="admin-agent-email" />
                <Row label="E-mail confirmé" value={d.agent.email_verified ? "Oui" : "Non"} />
                <Row label="Téléphone" value={d.agent.phone} />
                <Row label="Agence / réseau" value={d.agent.agency} />
                <Row label="Ville" value={`${d.agent.city} (${d.agent.department})`} />
                <Row label="Code de parrainage" value={d.agent.referral_code} />
                <Row label="Parrain" value={d.parrain?.name} />
                {d.referral_suspicious && <Badge label="Parrainage à vérifier (même IP)" color={C.warning} />}
              </Card>
              <Card>
                <h2 className="t-h3 mb-2">Commissions</h2>
                <Row label="En attente de validation" value={eur(d.commissions.pending)} />
                <Row label="Validées (à payer)" value={eur(d.commissions.validated)} />
                <Row label="Payées" value={eur(d.commissions.paid)} testID="admin-agent-paid" />
              </Card>
            </div>
            <div className="flex flex-col gap-4">
              <Card className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <h2 className="t-h3">Barème de commission</h2>
                  <Badge testID="admin-agent-tiers-type" label={d.custom_tiers ? "Défini" : "À définir"} color={d.custom_tiers ? C.success : C.warning} />
                </div>
                {!d.custom_tiers && <p data-testid="admin-agent-no-tiers" className="t-caption !text-[#F2994A]">Aucun barème : définissez-le avant d'enregistrer un contrat signé pour cet agent.</p>}
                {(d.custom_tiers ?? []).map((t, i) => (
                  <p key={i} data-testid={`admin-agent-tier-${i}`} className="t-caption !text-[#E0E0E0]">
                    {t.max !== null ? `De ${eur(t.min)} à ${eur(t.max)}` : `À partir de ${eur(t.min)}`} : {String(t.percent).replace(".", ",")} %
                  </p>
                ))}
                {d.can_view_rib && (
                  <Button testID="admin-agent-edit-tiers" title={d.custom_tiers ? "Modifier le barème" : "Définir le barème"} icon={Pencil} variant="secondary" className="mt-2"
                    onClick={() => { setTiers(d.custom_tiers ? toTierS(d.custom_tiers) : [{ min: "0", max: "", percent: "" }]); setTiersOpen(true); }} />
                )}
              </Card>
              <RibCard d={d} id={id} />
              <Card className="flex flex-col gap-2">
                <h2 className="t-h3">Filleuls ({d.filleuls.length})</h2>
                {d.filleuls.length === 0 ? <p className="t-caption">Aucun filleul.</p> : d.filleuls.map((f) => (
                  <Link key={f.id} to={`/admin/agent/${f.id}`} data-testid={`admin-agent-filleul-${f.id}`} className="focus-ring -mx-2 rounded-lg px-2 hover:bg-white/5">
                    <Row label={date(f.created_at)} value={f.name} />
                  </Link>
                ))}
              </Card>
            </div>
          </div>
          <h2 className="t-h2 mt-2">Dossiers ({d.recommendations.length})</h2>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {d.recommendations.map((r) => <DossierItem key={r.id} d={r} admin />)}
          </div>
        </div>
      )}
      <TiersSheet key={tiersOpen ? "open" : "closed"} open={tiersOpen} onClose={() => setTiersOpen(false)} d={d} id={id} tiers={tiers} setTiers={setTiers} onSaved={onSaved} />
    </div>
  );
}
